import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// IAP operational periods are Eastern-time 0700-to-0700 windows.
const OPERATIONAL_TZ = "America/New_York";
const OPERATIONAL_START = "07:00";

// One operational period = 0700 (Eastern) on the chosen date to 0700 (Eastern)
// the next day, returned as UTC ISO instants for the report API. Anchored to
// Eastern so the window is identical no matter where the report is run.
export const toOperationalPeriodWindow = (date: any) => {
  const day = dayjs(date?.toDate ? date.toDate() : date).format("YYYY-MM-DD");
  const start = dayjs.tz(`${day} ${OPERATIONAL_START}`, OPERATIONAL_TZ);
  return {
    startDate: start.toISOString(),
    endDate: start.add(1, "day").toISOString(),
  };
};

// The operational period currently in progress: today's 0700 ET window, or —
// when the clock has not reached 0700 yet — the one that started yesterday.
// Used as the default window for the ICD dashboard card.
export const currentOperationalPeriodWindow = () => {
  const todayWindow = toOperationalPeriodWindow(dayjs());
  if (dayjs().isBefore(dayjs(todayWindow.startDate))) {
    return toOperationalPeriodWindow(dayjs().subtract(1, "day"));
  }
  return todayWindow;
};

// API stock status comes as "In_Stock" / "Low_Stock" / "Out_of_Stock".
// The status colour helpers in utils/statusConstants expect spaced labels.
export const normalizeStockStatus = (status?: string) =>
  status ? status.replace(/_/g, " ") : "";

// Display a report date (visit / incident / order date) as MM/DD/YYYY.
// These are date-only values stored as midnight UTC — render in UTC so the
// calendar date never shifts a day for viewers behind UTC.
export const formatReportDate = (date?: string) =>
  date ? moment.utc(date).format("MM/DD/YYYY") : "-";

// Convert a DatePicker value to a UTC ISO instant anchored to the user's local
// day. The backend compares these directly, so the correct calendar day is
// returned regardless of server timezone (plain date-only strings were bucketed
// at the server's midnight, shifting results a day for US users).
export const toApiStartDate = (value?: any) =>
  value && moment(value).isValid()
    ? moment(value).startOf("day").toISOString()
    : undefined;

export const toApiEndDate = (value?: any) =>
  value && moment(value).isValid()
    ? moment(value).endOf("day").toISOString()
    : undefined;

// Map raw visit-type values from the EMS daily report to friendly labels
export const formatVisitType = (t?: string) =>
  t === "Scene"
    ? "EMS Scene"
    : t === "EMS_Visit"
      ? "EMS"
      : t === "WALK_In_Visit"
        ? "Walk In"
        : t || "-";

// Incident-report Visit Type column label — matches VISIT_TYPE_OPTIONS labels.
export const formatIncidentVisitType = (t?: string) =>
  t === "Scene" || t === "EMS_Visit"
    ? "EMS"
    : t === "WALK_In_Visit"
      ? "Walk In"
      : t || "-";

export const STOCK_STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "In Stock", value: "In_Stock" },
  { label: "Low Stock", value: "Low_Stock" },
  { label: "Out of Stock", value: "Out_of_Stock" },
];

// Inventory item types for FILTER dropdowns. Kept as a stable list so the
// report's Type filter never collapses to the currently selected value.
//
// This deliberately no longer mirrors CATEGORY_TYPE in the Add Inventory dialog
// (src/screens/inventory-screen/dialogs/AddNewInventory.tsx): "Not Medicine" is
// a filter-only value that returns everything which isn't Medicine. It is not a
// real category — the backend Types enum rejects it on create — so it must stay
// out of the create/edit form.
export const INVENTORY_TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Medicine", value: "Medicine" },
  { label: "Not Medicine", value: "Not Medicine" },
  { label: "Medical Supplies", value: "Medical Supplies" },
  { label: "Linen", value: "Linen" },
  { label: "General Supplies", value: "General Supplies" },
  { label: "Medical Equipment", value: "Medical Equipment" },
];

// Incident report visit-type filter. API value: "Scene" = EMS, "WALK_In_Visit" = Walk In.
export const VISIT_TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Walk In", value: "WALK_In_Visit" },
  { label: "EMS", value: "Scene" },
];

// Usage report type filter — Rx vs Non-Rx. Values are the exact strings the backend expects.
export const USAGE_TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Rx Order", value: "Rx Order" },
  { label: "Non Rx Order", value: "non-rx" },
];

export const ALL_FACILITIES_OPTION = { label: "All Facilities", value: "" };

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

// CSV header definitions (react-csv) -------------------------------------------------

export const CSV_HEADERS_INVENTORY = [
  { label: "Item Name", key: "itemName" },
  { label: "Item Code", key: "itemCode" },
  { label: "SKU Code", key: "skuCode" },
  { label: "Type", key: "type" },
  { label: "Unit Of Measure", key: "unitOfMeasure" },
  { label: "Threshold", key: "threshold" },
  { label: "Location", key: "facilityName" },
  { label: "Current Stock", key: "currentStock" },
  { label: "Stock Status", key: "status" },
  { label: "Total Stock (All Locations)", key: "totalStock" },
];

export const CSV_HEADERS_INCIDENT = [
  { label: "Patient Name", key: "patientName" },
  { label: "BSA ID", key: "bsaId" },
  { label: "Incident Location", key: "incidentLocation" },
  { label: "Visit Type", key: "visitType" },
  { label: "Chief Complaint", key: "chiefComplaint" },
  { label: "Incident Date", key: "incidentDate" },
  { label: "Facility", key: "facilityName" },
  { label: "Status", key: "status" },
];

export const CSV_HEADERS_USAGE = [
  { label: "Item Name", key: "itemName" },
  { label: "Type", key: "type" },
  { label: "Facility", key: "facilityName" },
  { label: "Total Quantity", key: "totalQuantity" },
];

export const CSV_HEADERS_DAILY_ORDERS = [
  { label: "Date", key: "date" },
  { label: "Patient Name", key: "patientName" },
  { label: "BSA ID", key: "bsaId" },
  { label: "Facility", key: "facilityName" },
  { label: "Order Type", key: "orderType" },
  { label: "Items", key: "items" },
  { label: "Quantity", key: "quantity" },
  { label: "Frequency", key: "frequency" },
  { label: "Status", key: "status" },
];

export const CSV_HEADERS_EMS_DAILY = [
  { label: "Visit Date", key: "visitDate" },
  { label: "Patient Name", key: "patientName" },
  { label: "BSA ID", key: "bsaId" },
  { label: "Visit Type", key: "visitType" },
  { label: "Incident Number", key: "incidentNumber" },
  { label: "Incident Location", key: "incidentLocation" },
  { label: "Incident Date", key: "incidentDate" },
  { label: "Chief Complaint", key: "chiefComplaint" },
  { label: "Facility", key: "facilityName" },
  { label: "Status", key: "status" },
];

// Render a report date-time (facility move timestamp) as MM/DD/YYYY hh:mm A.
// changedAt is a true instant (carries a time-of-day + Z), so display it in the
// viewer's LOCAL time — not UTC — matching the app's standard (date-only values
// render with moment.utc; absolute instants render with local moment()).
export const formatReportDateTime = (date?: string) =>
  date ? moment(date).format("MM/DD/YYYY hh:mm A") : "-";

// Friendly labels for the facility-history change type / reason values written
// by the backend (see patient_facility_history.changeType). Falls back to a
// title-cased version of any value not explicitly mapped.
const FACILITY_CHANGE_LABELS: Record<string, string> = {
  create: "Admitted",
  convert: "Admitted",
  manual: "Manual Update",
  jotform: "EMS Intake",
  transferOffProperty: "Transferred Off Property",
  transferToJHC: "Transferred to JHC",
  transferToClinic: "Admitted to Observation",
  discharged: "Discharged",
  facility_update: "Facility Update",
};

export const formatFacilityChange = (value?: string) => {
  if (!value) return "-";
  if (FACILITY_CHANGE_LABELS[value]) return FACILITY_CHANGE_LABELS[value];
  return value
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const CSV_HEADERS_FACILITY_PATIENT_HISTORY = [
  { label: "Patient Name", key: "patientName" },
  { label: "BSA ID", key: "bsaId" },
  { label: "Current Facility", key: "currentFacilityName" },
];

// ICD-10 / DSM report code-type filter. Omitting the value returns both.
export const ICD_CODE_TYPE_OPTIONS = [
  { label: "ICD-10 & DSM", value: "" },
  { label: "ICD-10", value: "ICD10" },
  { label: "DSM", value: "DSM" },
];

// Mirrors the on-screen ICD/DSM report columns.
export const CSV_HEADERS_ICD = [
  { label: "Code", key: "code" },
  { label: "Type", key: "codeType" },
  { label: "Diagnosis", key: "description" },
  { label: "Facility", key: "facilityName" },
  { label: "Times Recorded", key: "count" },
];

// Provider Patient Log — the encounter date as a plain calendar date.
//
// ASSUMES `encounterDate` is a true instant (a time-of-day + Z), so it renders
// in the viewer's LOCAL time, not UTC — a note written at 02:00 UTC belongs to
// the previous calendar day in Eastern time, and a training log must show the
// day the trainee actually worked. This matches formatReportDateTime.
//
// If the API ever changes it to a date-only midnight-UTC value (the shape
// visitDate / incidentDate use), switch this to formatReportDate — local
// rendering would otherwise shift every date a day back for US viewers.
export const formatEncounterDate = (date?: string) =>
  date ? moment(date).format("MM/DD/YYYY") : "-";

// Provider Patient Log export. The on-screen columns come first, in column
// order (BSA ID split out of the Patient cell), because this is the log the
// supervised-training programmes file. The audit fields the table only hints
// at — who documented vs who signed, and the visit type — follow.
export const CSV_HEADERS_PROVIDER_PATIENT_LOG = [
  { label: "Provider", key: "providerName" },
  { label: "Patient Name", key: "patientName" },
  { label: "BSA ID", key: "bsaId" },
  { label: "Date of Encounter", key: "encounterDate" },
  { label: "Facility/Location", key: "facilityName" },
  { label: "Chief Complaint", key: "chiefComplaint" },
  { label: "ICD-10 / DSM Diagnosis", key: "diagnosisText" },
  { label: "Note", key: "noteStatus" },
  { label: "Documented By", key: "documentedByName" },
  { label: "Signed By", key: "signedByName" },
  { label: "Visit Type", key: "visitType" },
];
